import React, { useRef, useEffect, useState, useCallback } from "react";
import { Howl, Howler } from 'howler';
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Play, Square, Volume2 } from 'lucide-react';

const baseAssetsPath = '/assets/';

export default function EffectButton({ effect, volume, setCategory, onPlayRemote, onStopRemote }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(effect.duration || null);
  const soundRef = useRef(null);
  const timerRef = useRef(null);

  // Escuchar eventos globales de reproducción/detención para sincronizar estado visual entre Desktop y Remote
  useEffect(() => {
    const handleGlobalPlay = (e) => {
      const isThis = String(e.detail?.id) === String(effect.id) || e.detail?.path === effect.path;
      if (isThis) {
        setIsPlaying(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        // En remoto, mantener marcado hasta que el anfitrión termine la reproducción (o failsafe generoso)
        if (onPlayRemote) {
          const durSeconds = effect.duration || e.detail?.duration || 10;
          const safetyTimeout = Math.max(durSeconds + 30, 45) * 1000;
          timerRef.current = setTimeout(() => {
            setIsPlaying(false);
          }, safetyTimeout);
        }
        // En Desktop: NO hay timeout fijo. Howler emite onend al finalizar el audio físico o se detiene con stop
      } else {
        setIsPlaying(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (soundRef.current && !onPlayRemote) {
          try { soundRef.current.stop(); } catch (err) {}
        }
      }
    };

    const handleGlobalStop = (e) => {
      const isThis = !e.detail?.id || String(e.detail?.id) === String(effect.id) || e.detail?.path === effect.path;
      if (isThis) {
        setIsPlaying(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        if (soundRef.current && !onPlayRemote) {
          try { soundRef.current.stop(); } catch (err) {}
        }
      }
    };

    window.addEventListener('garaoke_play_effect', handleGlobalPlay);
    window.addEventListener('garaoke_stop_effect', handleGlobalStop);
    return () => {
      window.removeEventListener('garaoke_play_effect', handleGlobalPlay);
      window.removeEventListener('garaoke_stop_effect', handleGlobalStop);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [effect.id, effect.path, effect.duration, onPlayRemote]);

  const applyVolume = useCallback((vol) => {
    if (!soundRef.current) return;
    soundRef.current._volume = vol;
    if (vol <= 1) {
      soundRef.current.volume(vol);
    }
    // Web Audio GainNode admite amplificación (> 1.0) y atenuación inmediata
    if (soundRef.current._sounds && soundRef.current._sounds.length > 0) {
      soundRef.current._sounds.forEach((s) => {
        s._volume = vol;
        if (s._node && s._node.gain && Howler.ctx) {
          try {
            s._node.gain.setValueAtTime(vol, Howler.ctx.currentTime);
          } catch {
            s._node.gain.value = vol;
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    if (onPlayRemote) return; // Modo remoto: no cargar ni reproducir audio localmente en el móvil

    const sound = new Howl({
      src: [`${baseAssetsPath}${effect.path}`],
      html5: false,
      volume: Math.min(1, volume),
      onload: () => {
        const d = sound.duration();
        if (d && !isNaN(d) && d > 0) {
          setDuration(Math.max(1, Math.round(d)));
        }
      },
      onend: () => {
        setIsPlaying(false);
        window.dispatchEvent(new CustomEvent('garaoke_stop_effect', {
          detail: { id: effect.id, path: effect.path }
        }));
      },
      onloaderror: (id, error) => {
        console.warn(`Audio ${effect.path} no disponible temporalmente:`, error);
      }
    });

    soundRef.current = sound;
    applyVolume(volume);

    if (sound.state() === 'loaded') {
      const d = sound.duration();
      if (d && !isNaN(d) && d > 0) {
        setDuration(Math.max(1, Math.round(d)));
      }
    }

    return () => {
      sound.stop();
      sound.unload();
    };
  }, [effect.id, effect.path, onPlayRemote]);

  // Sincronizar en tiempo real cualquier cambio en el slider de volumen
  useEffect(() => {
    if (!onPlayRemote) {
      applyVolume(volume);
    }
  }, [volume, applyVolume, onPlayRemote]);

  const playAudio = () => {
    if (onPlayRemote) {
      if (isPlaying) {
        setIsPlaying(false);
        if (timerRef.current) clearTimeout(timerRef.current);
        window.dispatchEvent(new CustomEvent('garaoke_stop_effect', { detail: { id: effect.id, path: effect.path } }));
        if (typeof onStopRemote === 'function') {
          onStopRemote(effect);
        }
      } else {
        setIsPlaying(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        const durSeconds = effect.duration || 10;
        const safetyTimeout = Math.max(durSeconds + 30, 45) * 1000;
        timerRef.current = setTimeout(() => {
          setIsPlaying(false);
        }, safetyTimeout);
        window.dispatchEvent(new CustomEvent('garaoke_play_effect', {
          detail: { id: effect.id, path: effect.path, duration: durSeconds }
        }));
        if (typeof onPlayRemote === 'function') {
          onPlayRemote(effect, volume);
        }
      }
      return;
    }

    // Modo Desktop
    if (isPlaying) {
      setIsPlaying(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      window.dispatchEvent(new CustomEvent('garaoke_stop_effect', { detail: { id: effect.id, path: effect.path } }));
      if (soundRef.current) {
        try { soundRef.current.stop(); } catch (err) {}
      }
      return;
    }

    if (soundRef.current) {
      setIsPlaying(true);
      applyVolume(volume);
      window.dispatchEvent(new CustomEvent('garaoke_play_effect', {
        detail: { id: effect.id, path: effect.path, duration: effect.duration }
      }));
      const soundId = soundRef.current.play();
      if (soundId !== null && soundId !== undefined) {
        const activeSound = soundRef.current._soundById(soundId);
        if (activeSound) {
          activeSound._volume = volume;
          if (activeSound._node && activeSound._node.gain && Howler.ctx) {
            try {
              activeSound._node.gain.setValueAtTime(volume, Howler.ctx.currentTime);
            } catch {
              activeSound._node.gain.value = volume;
            }
          }
        }
      }
    }
  };

  const categories = effect.categories || [];

  return (
    <Box 
      sx={{ 
        mb: '3px',
        px: 1,
        py: 0.5,
        borderRadius: '10px',
        background: isPlaying 
          ? 'linear-gradient(90deg, rgba(0, 255, 136, 0.12) 0%, rgba(18, 8, 32, 0.95) 100%)' 
          : 'rgba(255, 255, 255, 0.03)',
        border: isPlaying 
          ? '1px solid rgba(0, 255, 136, 0.45)' 
          : '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: isPlaying ? '0 0 12px rgba(0, 255, 136, 0.2)' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 0.8,
        transition: 'all 0.15s ease',
        '&:hover': {
          background: isPlaying ? 'rgba(0, 255, 136, 0.16)' : 'rgba(255, 255, 255, 0.05)',
          borderColor: isPlaying ? '#00ff88' : 'rgba(255, 255, 255, 0.12)'
        }
      }}
    >
      {/* Avatar estilo contacto con icono de audio */}
      <Box 
        sx={{ 
          width: 26, 
          height: 26, 
          flexShrink: 0, 
          borderRadius: '50%', 
          background: isPlaying ? 'rgba(0, 255, 136, 0.15)' : 'rgba(255, 255, 255, 0.06)',
          border: isPlaying ? '1px solid #00ff88' : '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          color: isPlaying ? '#00ff88' : 'rgba(255, 255, 255, 0.6)',
          transition: 'all 0.2s ease'
        }}
      >
        <Volume2 size={13} color={isPlaying ? '#00ff88' : '#b44dff'} />
      </Box>

      {/* Info principal: Título del efecto, duración y categoría */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            color: isPlaying ? '#00ff88' : '#ffffff', 
            fontSize: '0.78rem', 
            fontWeight: isPlaying ? 700 : 500, 
            lineHeight: 1.15,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {effect.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mt: 0.1 }}>
          {duration !== null && (
            <>
              <Typography 
                component="span"
                sx={{ 
                  fontSize: '0.67rem', 
                  color: 'rgba(255, 255, 255, 0.45)', 
                  fontWeight: 600,
                  lineHeight: 1,
                  background: 'rgba(255, 255, 255, 0.05)',
                  px: 0.5,
                  py: 0.1,
                  borderRadius: '3px'
                }}
              >
                {duration}s
              </Typography>
              {categories.length > 0 && (
                <Typography 
                  component="span" 
                  sx={{ 
                    fontSize: '0.62rem', 
                    color: 'rgba(255, 255, 255, 0.22)', 
                    lineHeight: 1, 
                    userSelect: 'none' 
                  }}
                >
                  •
                </Typography>
              )}
            </>
          )}
          {categories.map((cat, idx) => {
            const categoryName = typeof cat === 'string' ? cat : cat?.name;
            if (!categoryName) return null;
            return (
              <React.Fragment key={`${categoryName}-${idx}`}>
                <Typography 
                  component="span"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategory({ target: { value: categoryName } });
                  }}
                  sx={{ 
                    fontSize: '0.67rem', 
                    color: 'rgba(255, 255, 255, 0.45)', 
                    cursor: 'pointer',
                    lineHeight: 1,
                    transition: 'color 0.15s ease',
                    '&:hover': { color: '#b44dff', textDecoration: 'underline' }
                  }}
                >
                  {categoryName}
                </Typography>
                {idx < categories.length - 1 && (
                  <Typography 
                    component="span" 
                    sx={{ 
                      fontSize: '0.62rem', 
                      color: 'rgba(255, 255, 255, 0.22)', 
                      lineHeight: 1, 
                      userSelect: 'none' 
                    }}
                  >
                    •
                  </Typography>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Botón de Play unificado con icono y color idénticos a PlayListCard */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Tooltip title={isPlaying ? "Detener efecto" : "Reproducir efecto"}>
          <IconButton 
            size="small" 
            onClick={playAudio}
            sx={{ 
              p: '4px', 
              borderRadius: '6px',
              color: isPlaying ? '#00ff88' : 'rgba(255, 255, 255, 0.7)', 
              background: isPlaying ? 'rgba(0, 255, 136, 0.15)' : 'transparent',
              '&:hover': { 
                color: '#00ff88',
                background: 'rgba(0, 255, 136, 0.2)' 
              } 
            }}
          >
            {isPlaying ? (
              <Square size={14} fill="#00ff88" />
            ) : (
              <Play size={15} fill="currentColor" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
